import { forwardRef } from "react";
import { X } from "lucide-react";

import { Icd10Combobox } from "./Icd10Combobox";
import { Badge } from "@/components/ui/badge";
import type { Diagnosis } from "@/shared/types/encounter";
import { randomUuid } from "../../../shared/randomUuid";

/**
 * Chẩn đoán (ICD-10) — the diagnosis list (TD-7b, #18800).
 *
 * <p><b>Owns the list RULES, not the persistence.</b> Deduplication and the "promote the next
 * diagnosis to primary" rule moved here, because they are facts about a diagnosis list and belong
 * wherever that list is edited. Saving stays with the workspace, which debounces it alongside the
 * other encounter edits — splitting one debounced save across two owners is how a screen starts
 * racing itself.
 *
 * <p>Forwards a ref because signing scrolls here when there is no diagnosis: the doctor is being
 * told to look at THIS section, so the section has to be addressable.
 */
export const DiagnosisPanel = forwardRef<
  HTMLDivElement,
  {
    diagnoses: Diagnosis[];
    /** Called with the whole next list — the workspace decides when it reaches the server. */
    onChange: (next: Diagnosis[]) => void;
    readOnly: boolean;
    /** True once signing has been refused for want of a diagnosis. */
    error: boolean;
  }
>(function DiagnosisPanel({ diagnoses, onChange, readOnly, error }, ref) {
  function add(code: string, name: string) {
    const icd10Code = code.trim() || null;
    const label = name.trim();
    if (!label) return;
    // Bỏ trùng: cùng mã ICD-10 (nếu có) hoặc cùng nhãn.
    if (diagnoses.some((d) => (icd10Code ? d.icd10Code === icd10Code : d.label === label))) {
      return;
    }
    onChange([
      ...diagnoses,
      {
        id: randomUuid(),
        icd10Code,
        label,
        primary: diagnoses.length === 0,
        note: null,
      },
    ]);
  }

  function remove(dxId: string) {
    const next = diagnoses.filter((d) => d.id !== dxId);
    // Nếu bỏ chẩn đoán chính, chuyển chẩn đoán còn lại đầu tiên thành chính.
    if (next.length > 0 && !next.some((d) => d.primary)) {
      next[0] = { ...next[0], primary: true };
    }
    onChange(next);
  }

  const empty = diagnoses.length === 0;

  return (
    <>
      <div
        ref={ref}
        className={
          error && empty ? "-mx-1 mb-1 rounded-md p-1 ring-2 ring-destructive" : undefined
        }
      >
        {!readOnly && (
          <div className="mb-3">
            <Icd10Combobox onPick={(entry) => add(entry.code, entry.labelVi)} />
          </div>
        )}
        {error && empty && (
          <p className="mb-2 text-sm font-medium text-destructive">
            Cần ít nhất 1 chẩn đoán trước khi hoàn tất khám.
          </p>
        )}
      </div>
      {!empty ? (
        <div className="space-y-2">
          {diagnoses.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
            >
              <span>
                {d.icd10Code && (
                  <span className="font-medium text-muted-foreground">{d.icd10Code} · </span>
                )}
                {d.label}
                {d.primary && (
                  <Badge className="ml-2" variant="default">
                    Chính
                  </Badge>
                )}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => remove(d.id)}
                  aria-label="Xoá chẩn đoán"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Chưa có chẩn đoán.</p>
      )}
    </>
  );
});
